import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Steps,
  Form,
  Button,
  List,
  Row,
  Col,
  notification,
  message,
  Popconfirm,
} from "antd";
import { useParams } from "react-router-dom";

import axiosInstance from "utilities/axios";

import { EquipmentSelector } from "components/EquipmentSelector";
import SpecificDetails from "./SpecificDetails";
import useRoleCheck from "utilities/hooks/roleCheck";
import { has } from "lodash";

function AddEquipment({ isVisible, onClose, onAdd, migratedClinic, requests }) {
  const { hasPermission } = useRoleCheck();

  // A modal that has multiple phases depending on the clinic's migration status - if
  const { clinic_no } = useParams();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(hasPermission(['Audiology.Admin', 'Audiology.Write']));

  // return the count of requests where requestType is "New Equipment" or "Transfer" and Status is "Shipped" or "Approved"
  const addableRequests = useMemo(() => {
    var relevantRequests = requests.filter((request) => {
      return (
        (request.requestType === "New Equipment" ||
          request.requestType === "Transfer") &&
        (request.currentStatus === "Shipped" ||
          request.currentStatus === "Approved")
      );
    });
    return relevantRequests;
  }, [requests]);

  const addTypeDefault = useMemo(() => {
    if ((migratedClinic || permission) && addableRequests.length > 0) {
      return null;
    } else if (addableRequests.length > 0) {
      return "request";
    } else {
      return "add";
    }
  }, [migratedClinic, addableRequests]);
  
  const [addType, setAddType] = useState(addTypeDefault);

  const [selectedRequest, setSelectedRequest] = useState(null);

  const totalSteps = useMemo(() => {
    let steps = 1;
    if (addableRequests.length > 0 && (migratedClinic || permission)) steps += 1;
    if (addType === "request") steps += 1;
    return steps;
  }, [addableRequests, addType, migratedClinic, permission]);

  const selectedStep = () => {
    // possible values are 0, 1, 2
    // Current value depends on how many steps there are in the modal
    // if no addable requests then the modal will only have one step
    // if there are addable requests but not migratedClinic then the modal will have two steps
    // if there are addable requests and migratedClinic then the modal will have three steps

    if (selectedRequest) {
      return totalSteps - 1;
    } else if (addType === "add") {
      return totalSteps - 1;
    } else if (!addType) {
      return 0;
    } else if (addType === "request" && (!migratedClinic && !permission)) {
      return 0;
    } else {
      return 1;
    }
  };

  const handleClose = () => {
    onClose();
    form.resetFields();
    setSelectedRequest(null);
    setAddType(addTypeDefault);
  };

  const handleSubmit = (values) => {
    setLoading(true);
    // validate equipment id otherwise dispaly message
    if (!values.equipmentId && !selectedRequest) {
      message.error("Please select a piece of equipment.");
      setLoading(false);
      return;
    }
    const payload = {
      clinicNo: clinic_no,
      ...values,
      requestId: selectedRequest ? selectedRequest.id : null,
    };
    var endpoint = "/equipment/inventory";
    if (
      selectedRequest?.requestType === "Transfer" ||
      selectedRequest?.requestType === "Repair"
    ) {
      endpoint = `/equipment/requests/${selectedRequest.id}/complete`;
    } else if (selectedRequest?.requestType === "New Equipment") {
      endpoint = "/equipment/inventory";
    }
    axiosInstance
      .post(endpoint, payload)
      .then(() => {
        notification.success({
          message: "Equipment Added",
          description: "You have successfully added equipment to the clinic.",
        });
        onAdd();
        handleClose();
      })
      .catch((error) => {
        console.log(payload);
        notification.error({
          message: "Error",
          description: error.message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    setAddType(addTypeDefault);
  }, [addTypeDefault]);

  return (
    <Modal
      title="Add Equipment"
      open={isVisible}
      onCancel={handleClose}
      width={totalSteps > 2 ? 800 : 600}
      footer={
        //   if selectedStep is greater than 1, include a back button
        <>
          {/* cancel button always visible */}
          <Button onClick={handleClose}>Cancel</Button>

          {selectedStep() > 0 && totalSteps > 1 && (
            <Button
              onClick={() => {
                if (selectedRequest) {
                  setSelectedRequest(null);
                } else {
                  setAddType(null);
                }
              }}
            >
              Back
            </Button>
          )}
          {/* if on the final step */}
          {selectedStep() === totalSteps - 1 && (
            (!migratedClinic && permission && addType === "add") ?
              <Popconfirm title="Are you sure you want to add this equipment to this clinic?" onConfirm={form.submit}>
                <Button type="primary" loading={loading}>
                  Add Equipment
                </Button>
              </Popconfirm>
            :
            <Button type="primary" loading={loading} onClick={form.submit}>
              Add Equipment
            </Button>
          )}
        </>
      }
    >
      {addableRequests && addableRequests.length > 0 && (
        <Steps
          current={selectedStep()}
          size="small"
          style={{ marginBottom: "20px" }}
        >
          {(migratedClinic || permission) && addableRequests && (
            <Steps.Step title="Choose Add Type" />
          )}
          {addType === "request" && <Steps.Step title="Choose Request" />}

          <Steps.Step title="Add Equipment Details" />
        </Steps>
      )}
      {(addType === null && hasPermission(['Audiology.Admin', 'Audiology.Write'])) && (
        // two vertical buttons
        <Row gutter={16}>
          <Col span={12}>
            <Button
              block
              type="primary"
              onClick={() => {
                setAddType("add");
              }}
            >
              Add New Equipment
            </Button>
          </Col>
          <Col span={12}>
            <Button
              block
              type="primary"
              onClick={() => {
                setAddType("request");
              }}
            >
              Add Requested Equipment
            </Button>
          </Col>
        </Row>
      )}

      {addType === "request" && !selectedRequest && (
        // a list of requests to select
        <List
          itemLayout="horizontal"
          dataSource={addableRequests}
          renderItem={(request) => (
            <List.Item
              actions={[
                <Button
                  type="primary"
                  onClick={() => {
                    setSelectedRequest(request);
                  }}
                >
                  Add
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={`${request.equipment.category} - ${request.equipment.brand} ${request.equipment.model}`}
                description={request.requestType}
              />
            </List.Item>
          )}
        />
      )}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {addType === "add" && (
          <Form.Item name="equipmentId">
            <EquipmentSelector />
          </Form.Item>
        )}
        {((addType === "request" &&
          selectedRequest &&
          selectedRequest.requestType === "New Equipment") ||
          addType === "add") && <SpecificDetails />}
      </Form>
    </Modal>
  );
}

export default AddEquipment;
